import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    FormControlLabel,
    IconButton,
    Popover,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { Settings as SettingsIcon } from '@material-ui/icons';
import useSettings from 'src/hooks/useSettings';
import SITE_SETTINGS from 'src/constants/whiteLabel';
import i18next from 'src/utils/i18next';
import { useSnackbar } from 'notistack';
import DpxSwitch from 'src/components/DpxSwitch';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import api from 'src/api/api';
import { HEADERS } from 'src/constants/headers';
import {
    USER_LANGUAGE,
    USER_LANGUAGE_STORAGE_KEY,
} from 'src/modules/user/constants';

const useStyles = makeStyles((theme) => ({
    popover: {
        width: 320,
        padding: theme.spacing(2),
    },
}));

const Settings = () => {
    const classes = useStyles();
    const ref = useRef(null);
    const { settings, saveSettings } = useSettings();
    const { user } = useAuth();
    const [language, setLanguage] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isOpen, setOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [values, setValues] = useState({
        direction: settings.direction,
        responsiveFontSizes: settings.responsiveFontSizes,
        theme: settings.theme,
    });

    useEffect(() => {
        const preferredLanguage =
            user?.preferredLanguage
            || localStorage.getItem(USER_LANGUAGE_STORAGE_KEY)
            || USER_LANGUAGE.ENGLISH;

        localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, preferredLanguage);
        i18next.changeLanguage(preferredLanguage);
        setLanguage(preferredLanguage === USER_LANGUAGE.SPANISH);
    }, [user?.preferredLanguage]);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (field, value) => {
        setValues({
            ...values,
            [field]: value,
        });
    };

    const handleClearCache = () => {
        let lenguaje = localStorage.getItem('language');
        let lan = localStorage.getItem('i18nextLng');
        let token = localStorage.getItem('token');
        let settings = localStorage.getItem('settings');

        localStorage.clear();

        localStorage.setItem('language', lenguaje);
        localStorage.setItem('i18nextLng', lan);
        localStorage.setItem('token', token);
        localStorage.setItem('settings', settings);

        enqueueSnackbar(t('SnackBar.Cache'), {
            variant: 'info',
        });
    };

    const handleSave = async () => {
        const preferredLanguage = language
            ? USER_LANGUAGE.SPANISH
            : USER_LANGUAGE.ENGLISH;

        setSaving(true);
        try {
            await api.put(
                '/auth/updatedetails',
                { preferredLanguage },
                HEADERS()
            );
            localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, preferredLanguage);
            await i18next.changeLanguage(preferredLanguage);
            saveSettings(values);
            enqueueSnackbar(t('Settings.LanguageSaved'), { variant: 'success' });
            setOpen(false);
        } catch (error) {
            enqueueSnackbar(t('Settings.LanguageSaveError'), { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    let themesList;
    if (Object.keys(SITE_SETTINGS.themes).length > 0) {
        themesList = Object.values(SITE_SETTINGS.themes);
    }

    return (
        <>
            <Tooltip title={t('Settings.Settings')}>
                <IconButton
                    style={{ color: 'white' }}
                    onClick={handleOpen}
                    ref={ref}
                >
                    <SettingsIcon />
                </IconButton>
            </Tooltip>
            <Popover
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                classes={{ paper: classes.popover }}
                anchorEl={ref.current}
                onClose={handleClose}
                open={isOpen}
            >
                <Typography variant="h4" color="textPrimary">
                    {t('Settings.Settings')}
                </Typography>

                <Box mt={2} px={1}>
                    <FormControlLabel
                        control={
                            <DpxSwitch
                                edge="start"
                                checked={language}
                                onChange={(_) => {
                                    setLanguage(!language);
                                }}
                                inputProps={{
                                    'aria-label': 'primary checkbox',
                                }}
                                name="language"
                                color="primary"
                            />
                        }
                        label="EN/ES"
                    />
                </Box>

                <Box mt={2} px={1}>
                    <TextField
                        fullWidth
                        label={t('Settings.Theme')}
                        name="theme"
                        onChange={(event) =>
                            handleChange('theme', event.target.value)
                        }
                        select
                        SelectProps={{ native: true }}
                        value={values.theme}
                        variant="outlined"
                    >
                        {themesList.map((tema, index) => (
                            <option key={index} value={tema.theme}>
                                {tema.name}
                            </option>
                        ))}
                    </TextField>
                </Box>
                <Box mt={2} px={1}>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleClearCache}
                    >
                        {t('Settings.Cache')}
                    </Button>
                </Box>
                <Box mt={2} px={1}>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {t('Settings.Save')}
                    </Button>
                </Box>
            </Popover>
        </>
    );
};

export default Settings;
